import { computed, onBeforeUnmount } from 'vue'
import { debouncedWatch } from '@vueuse/core'

import { WidgetExtended } from '@/types/dashboard'

import useWidgetData from '@/composables/useWidgetData'
import { editableWidgetId } from '@/composables/useWidgetEditModal'
import { getDefaultTimeDelay } from '@/helpers/widgetUtils'
// import { isEqual } from 'lodash'

const MAX_INT32 = 2 ** 31 - 1
const REQUEST_TIME_BUFFER = 1.2

export default function <
    T,
    P extends { [Key in K]: WidgetExtended },
    K extends keyof P,
> (props: Readonly<P>, key: K) {
    // Composables
    const {
        widgetRef,
        data,
        dataLoaded,
        isLoading,
        fetchData
    } = useWidgetData<T, P, K>(props, key)

    // Data
    let fetchDataInterval: number | null = null
    let isDestroyed = false

    // Computed
    const isWidgetInEdit = computed(() => {
        return editableWidgetId.value === widgetRef.value.WidgetID
    })

    // Methods
    async function getDataWithRefreshInterval () {
        if (isDestroyed) return

        if (fetchDataInterval !== null) {
            clearTimeout(fetchDataInterval)
            fetchDataInterval = null
        }

        const startTime = Date.now()

        await fetchData()

        if (isDestroyed) return

        const endTime = Date.now()

        const requestTimeDiff = endTime - startTime

        const defaultRefreshDelay = getDefaultTimeDelay()
        const widgetLayoutRefreshInterval = (widgetRef.value.WidgetLayout?.DefaultRefreshInterval || 0) * 1000
        const refreshIntervalFromWidget = widgetLayoutRefreshInterval || defaultRefreshDelay
        // Calculate the minimum refresh interval based on the time it took to fetch the data plus some buffer
        const minimumRefreshInterval = requestTimeDiff * REQUEST_TIME_BUFFER
        // If the refresh interval from the widget is less than the time it took to fetch the data, then use the minimum refresh interval
        const refreshInterval = refreshIntervalFromWidget < minimumRefreshInterval
            ? minimumRefreshInterval
            : refreshIntervalFromWidget
        const maxSafeRefreshInterval = refreshInterval > MAX_INT32
            ? MAX_INT32
            : refreshInterval

        if (maxSafeRefreshInterval) {
            fetchDataInterval = +setTimeout(
                getDataWithRefreshInterval,
                maxSafeRefreshInterval
            )
        }
    }

    // Hooks
    onBeforeUnmount(() => {
        isDestroyed = true
        if (fetchDataInterval !== null) {
            clearTimeout(fetchDataInterval)
            fetchDataInterval = null
        }
    })

    // Watchers
    debouncedWatch(
        widgetRef,
        async (newWidget, /*oldWidget*/) => {
            if (!isDestroyed && !isWidgetInEdit.value) {
                // isEqual was added for request cancel optimization, but it prevents the initial load of the widgets data, check in future
                if (!newWidget /*|| isEqual(oldWidget, newWidget)*/) {
                    return
                }
                await getDataWithRefreshInterval()
            }
        },
        {
            deep: true,
            immediate: true,
            /**
             * Set that much because we have an issue that after user changed something in widget edit we have 2 simultaneous calls to fetch it's updated data.
             * That happens, because the widgetRef we are getting from computed widgetLocalData which gives either data from props (defined in DB), or local temporal model depending on if in edit or not
             * So when user saves the edit modal it triggers this watcher because user no longer in edit mode, the widgetLocalData computed recalculated and now the widgetRef is not localModel but data from DB
             * After which we are sending update request and get updated widget data from DB request, which triggers this watcher again.
             */
            debounce: 1000
        }
    )

    return {
        isLoading,
        data,
        dataLoaded,
        widgetRef
    }
}
